import { type QueryFunction, type QueryKey } from '@tanstack/react-query';
import transformKeyCase from 'Utils/transformKeyCase';
import { type AnyObject } from '@Types';
import { baseUrl, buildAuthHeaders, serializeParams } from './queryShared';
import { snakeCaseApiParams } from './convertCasing';
import { formatTanStackErrorResponse, formatTanStackFetchError } from './formatTanStackErrorResponse';
import { type BaseTanStackQueryFnArgs } from './TanStackApi.types';

type TanStackQueryKey = [string, BaseTanStackQueryFnArgs | undefined];

const ensureArguments = (queryKey: QueryKey): BaseTanStackQueryFnArgs => {
    const [, rawArgs] = queryKey as TanStackQueryKey;

    if (!rawArgs) {
        throw new Error('TanStack query must be invoked with a configuration object.');
    }

    return rawArgs;
};

// TanStack does not have a timeout option, so we implement that ourselves here using
// the signal that tanstack does provide
function abortOnTimeout(timeout: number, externalSignal?: AbortSignal) {
    const controller = new AbortController();
    const abortWithExternalReason = () => controller.abort(externalSignal?.reason ?? undefined);

    if (externalSignal) {
        if (externalSignal.aborted) {
            controller.abort(externalSignal.reason);
        } else {
            externalSignal.addEventListener('abort', abortWithExternalReason, { once: true });
        }
    }

    const timeoutId = window.setTimeout(() => controller.abort('TIMEOUT_ERROR'), timeout);

    return { controller, abortWithExternalReason, timeoutId };
}

const createRequestContext = (args: BaseTanStackQueryFnArgs, timeout: number, externalSignal?: AbortSignal) => {
    const { url, params, headers: additionalHeaders } = args;
    const queryString = params ? serializeParams(params) : '';
    const fullUrl = `${baseUrl}${url}${queryString ? `?${queryString}` : ''}`;

    const headers = buildAuthHeaders(additionalHeaders);

    const { controller, abortWithExternalReason, timeoutId } = abortOnTimeout(timeout, externalSignal);

    return { fullUrl, headers, controller, abortWithExternalReason, timeoutId };
};

const buildFetchOptions = (
    args: BaseTanStackQueryFnArgs,
    headers: Headers,
    controller: AbortController,
): RequestInit => ({
    method: args.method ?? 'GET',
    headers,
    body: buildRequestBody(args.body, headers),
    signal: controller.signal,
});

const parseResponse = async <TData>(response: Response, endpointPath: string) => {
    if (!response.ok) {
        throw await formatTanStackErrorResponse(response, endpointPath);
    }

    if (response.status === 204) return undefined as TData;
    if (!isJsonResponse(response)) return (await response.text()) as unknown as TData;

    const data = await response.json();

    if (data && typeof data === 'object') {
        // We can't call convertQueryCasing directly because TanStack hands us the raw payload,
        // not the RTKQuery result object that function expects. Instead we reuse the underlying
        // transform to preserve the casing behaviour.
        return transformKeyCase<TData>(data as AnyObject, { to: 'camelCase' });
    }

    return data as TData;
};

const detachExternalSignal = (signal: AbortSignal | undefined, handler: () => void) => {
    if (signal) signal.removeEventListener('abort', handler);
};

export function buildTanStackBaseBackRoyalQueryFn<TData = unknown>({
    timeout = 30000,
}: {
    timeout?: number;
} = {}): QueryFunction<TData, QueryKey> {
    return async ({ queryKey, signal }) => {
        const args = ensureArguments(queryKey);
        const { url } = args;
        const { fullUrl, headers, controller, abortWithExternalReason, timeoutId } = createRequestContext(
            args,
            timeout,
            signal,
        );

        try {
            return fetchAndParseResponse<TData>({ url, fullUrl, args, headers, controller });
        } finally {
            clearTimeout(timeoutId);
            detachExternalSignal(signal, abortWithExternalReason);
        }
    };
}

async function fetchAndParseResponse<TData>({
    url,
    fullUrl,
    args,
    headers,
    controller,
}: {
    url: string;
    fullUrl: string;
    args: BaseTanStackQueryFnArgs;
    headers: Headers;
    controller: AbortController;
}) {
    let rawResponse: Response;
    try {
        // fetch throws on network-level failures; we convert those to the shared QueryError shape
        // so TanStack's retry callback can handle them uniformly with RTK Query errors.
        rawResponse = await fetch(fullUrl, buildFetchOptions(args, headers, controller));
    } catch (error) {
        throw formatTanStackFetchError(error, url);
    }

    const parsedResponse = (await parseResponse<TData>(rawResponse, url)) as TData;
    return parsedResponse;
}

function buildRequestBody(body: BaseTanStackQueryFnArgs['body'], headers: Headers): RequestInit['body'] {
    if (body == null || body === '') return undefined;

    if (body instanceof FormData) {
        return body;
    }

    if (typeof body === 'string') {
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }
        return body;
    }

    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    // RTK Query applies snake_casing inside convertQueryCasing. TanStack does not, so we replicate
    // just the request-side behaviour here to keep parity with existing endpoints.
    return JSON.stringify(snakeCaseApiParams(body));
}

function isJsonResponse(response: Response) {
    const contentType = response.headers.get('content-type');
    return contentType ? contentType.includes('application/json') : false;
}
